import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { activityById, activityDefinitions, focusOptions, goalsForFocus } from '../../data/activityCatalog';
import { useActivityPlan } from '../../contexts/ActivityPlanContext';
import { activityIconClasses } from '../../utils/activityStyles';
import { dailyTarget } from '../../utils/activityProgress';
import { DatabaseImportStep, emptyImportState, type ImportKind, type ImportResult, type ImportState } from './DatabaseImportStep';
import type { ActivityCategory, ActivityGoal } from '../../types/activity';
interface PerfectWeekWizardProps {
  onComplete: () => void;
}
const steps = [{
  id: 'focus',
  label: 'Focus'
}, {
  id: 'activities',
  label: 'Activities'
}, {
  id: 'targets',
  label: 'Targets'
}, {
  id: 'rhythm',
  label: 'Rhythm'
}, {
  id: 'review',
  label: 'Review'
}, {
  id: 'import',
  label: 'Import'
}] as const;
export function PerfectWeekWizard({
  onComplete
}: PerfectWeekWizardProps) {
  const {
    plan,
    savePlan
  } = useActivityPlan();
  const [stepIndex, setStepIndex] = useState(0);
  const [focus, setFocus] = useState(plan.focus);
  const [workdays, setWorkdays] = useState(plan.workdays);
  const [goals, setGoals] = useState<ActivityGoal[]>(plan.goals);
  const [imports, setImports] = useState<ImportState>(emptyImportState);
  const step = steps[stepIndex];
  const enabledGoals = useMemo(() => goals.filter(goal => goal.enabled), [goals]);
  const applyFocus = (focusId: string) => {
    setFocus(focusId);
    const suggested = goalsForFocus(focusId);
    setGoals(current => {
      const noneEnabled = current.every(goal => !goal.enabled);
      return current.map(goal => {
        const match = suggested.find(item => item.category === goal.category);
        if (!match) return goal;
        return {
          ...goal,
          weeklyTarget: match.weeklyTarget,
          enabled: noneEnabled ? true : goal.enabled
        };
      });
    });
  };
  const toggleGoal = (category: ActivityCategory) => {
    setGoals(current => current.map(goal => goal.category === category ? {
      ...goal,
      enabled: !goal.enabled
    } : goal));
  };
  const setTarget = (category: ActivityCategory, value: number) => {
    setGoals(current => current.map(goal => goal.category === category ? {
      ...goal,
      weeklyTarget: Math.max(1, Math.min(99, value))
    } : goal));
  };
  const canAdvance = step.id !== 'activities' || enabledGoals.length > 0;
  const importedCount = (imports.leads?.recordCount ?? 0) + (imports.realtors?.recordCount ?? 0);
  const handleSave = () => {
    savePlan({
      focus,
      workdays,
      goals
    });
    onComplete();
  };
  return <div className="rounded-2xl border border-navy-100 bg-white shadow-card" data-id="element-402" data-mp="pftftp485mqd">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-navy-100 px-5 py-4" data-id="element-403" data-mp="kg711b8qzyfg">
        {steps.map((item, index) => {
        const active = index === stepIndex;
        const done = index < stepIndex;
        return <li key={item.id} className="flex items-center gap-2" data-id="element-404" data-mp="313f2ijplc00n" data-mpi={index}>
              <button type="button" onClick={() => setStepIndex(index)} aria-current={active ? 'step' : undefined} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${active ? 'bg-navy-800 text-white' : done ? 'text-success-700' : 'text-navy-400'}`} data-id="element-405" data-mp="3618572roqbw6">
                <span className="inline-flex h-4 w-4 items-center justify-center" data-id="element-406" data-mp="3tq9jh6n78h9d">
                  {done ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-407" data-mp="1ufi4xzjrb0oe" /> : index + 1}
                </span>
                {item.label}
              </button>
              {index < steps.length - 1 ? <span className="h-px w-4 bg-navy-200" aria-hidden="true" data-id="element-408" data-mp="3kvtwmse1ivhe" /> : null}
            </li>;
      })}
      </ol>

      <div className="px-5 py-6" data-id="element-409" data-mp="2owc28vknjka">
        {step.id === 'focus' ? <fieldset data-id="element-410" data-mp="3uujfandrgjyp">
            <legend className="text-lg font-bold text-navy-900" data-id="element-411" data-mp="3utzu8xstdfgg">
              What are you trying to grow this quarter?
            </legend>
            <p className="mt-1 text-sm text-navy-500" data-id="element-412" data-mp="324t16ec4pi2l">
              We use this to suggest starting targets. You can change every number
              on the next steps.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-413" data-mp="3fy26a2t1nhfb">
              {focusOptions.map(option => {
            const active = option.id === focus;
            return <label key={option.id} className={`cursor-pointer rounded-xl border p-4 ${active ? 'border-brand-400 bg-brand-50 ring-1 ring-brand-200' : 'border-navy-200 hover:bg-navy-50'}`} data-id="element-414" data-mp="2ndtrezzk4nhf">
                    <span className="flex items-start gap-2" data-id="element-415" data-mp="1zczx76cubl3d">
                      <input type="radio" name="focus" value={option.id} checked={active} onChange={() => applyFocus(option.id)} className="mt-1 h-4 w-4 accent-brand-600" data-id="element-416" data-mp="1zadqf8xrx9m7" />
                      <span data-id="element-417" data-mp="2i3hlmyqn3uix">
                        <span className="block text-sm font-semibold text-navy-900" data-id="element-418" data-mp="2i26h2wpdeiqr">
                          {option.label}
                        </span>
                        <span className="mt-1 block text-xs text-navy-600" data-id="element-419" data-mp="16n1dlqhf0g63">
                          {option.description}
                        </span>
                      </span>
                    </span>
                  </label>;
          })}
            </div>
          </fieldset> : null}

        {step.id === 'activities' ? <fieldset data-id="element-420" data-mp="39cb1vwvoardd">
            <legend className="text-lg font-bold text-navy-900" data-id="element-421" data-mp="34jhv73gn98yn">
              Which activities belong in your perfect week?
            </legend>
            <p className="mt-1 text-sm text-navy-500" data-id="element-422" data-mp="2zjlupe75nznu">
              Turn off anything that is not how you actually build business.
            </p>
            <ul className="mt-4 space-y-2" data-id="element-423" data-mp="2qjqeguyqtegy">
              {activityDefinitions.map(definition => {
            const goal = goals.find(item => item.category === definition.id);
            const Icon = definition.icon;
            const enabled = goal?.enabled ?? false;
            return <li key={definition.id} data-id="element-424" data-mp="jry0hqjj0l8q">
                    <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 ${enabled ? 'border-brand-200 bg-brand-50/40' : 'border-navy-200 hover:bg-navy-50'}`} data-id="element-425" data-mp="eu67sub24v3h">
                      <input type="checkbox" checked={enabled} onChange={() => toggleGoal(definition.id)} className="mt-1 h-4 w-4 accent-brand-600" data-id="element-426" data-mp="w38f4lqs9aeb" />
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${activityIconClasses[definition.tone]}`} data-id="element-427" data-mp="8dxmxsni0obs">
                        <Icon className="h-4 w-4" aria-hidden="true" data-id="element-428" data-mp="dfd7ljlrkstn" />
                      </span>
                      <span className="min-w-0" data-id="element-429" data-mp="i9qxx7lkk9rg">
                        <span className="flex flex-wrap items-center gap-2" data-id="element-430" data-mp="1or4btqgcr2b3">
                          <span className="text-sm font-semibold text-navy-900" data-id="element-431" data-mp="1tqqy0pe5rsw1">
                            {definition.label}
                          </span>
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-navy-400" data-id="element-432" data-mp="2et8bzo2mtxcd">
                            {definition.logging === 'manual' ? 'Manual log' : 'Auto-assisted'}
                          </span>
                        </span>
                        <span className="mt-0.5 block text-xs text-navy-600" data-id="element-433" data-mp="2xlixc418tcmq">
                          {definition.description}
                        </span>
                      </span>
                    </label>
                  </li>;
          })}
            </ul>
            {enabledGoals.length === 0 ? <p className="mt-3 text-xs font-semibold text-danger-600" data-id="element-434" data-mp="32lx7psqbgynx">
                Pick at least one activity to build a plan.
              </p> : null}
          </fieldset> : null}

        {step.id === 'targets' ? <div data-id="element-435" data-mp="1897b338kdbcu">
            <h2 className="text-lg font-bold text-navy-900" data-id="element-436" data-mp="3ju7xhd9a5grh">
              How many per week?
            </h2>
            <p className="mt-1 text-sm text-navy-500" data-id="element-437" data-mp="3otu6tblzmcv0">
              Set the weekly commitment. We split it across your working days.
            </p>
            <ul className="mt-4 space-y-3" data-id="element-438" data-mp="3a44s0dkz2ac9">
              {enabledGoals.map(goal => {
            const definition = activityById[goal.category];
            return <li key={goal.category} className="rounded-xl border border-navy-100 p-4" data-id="element-439" data-mp="312x7cybdp80n">
                    <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-440" data-mp="2r5k6d11unx60">
                      <div data-id="element-441" data-mp="2r7wrto6tgpnt">
                        <p className="text-sm font-semibold text-navy-900" data-id="element-442" data-mp="2w43tv5w3dh0h">
                          {definition.label}
                        </p>
                        <p className="text-xs text-navy-500" data-id="element-443" data-mp="peyzrukv613x">
                          {definition.cadence === 'daily' ? `About ${dailyTarget(goal, workdays)} ${definition.unit} per working day` : `Tracked weekly`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-444" data-mp="qdc35gpsqv2y">
                        <button type="button" onClick={() => setTarget(goal.category, goal.weeklyTarget - 1)} aria-label={`Decrease ${definition.label} target`} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-navy-200 text-navy-700 hover:bg-navy-50" data-id="element-445" data-mp="qf5a6x4bnhbh">
                          <MinusIcon className="h-4 w-4" aria-hidden="true" data-id="element-446" data-mp="1e1vb1v4xp5ui" />
                        </button>
                        <span className="w-10 text-center text-lg font-bold tabular-nums text-navy-900" data-id="element-447" data-mp="3tt99tfor1kwj">
                          {goal.weeklyTarget}
                        </span>
                        <button type="button" onClick={() => setTarget(goal.category, goal.weeklyTarget + 1)} aria-label={`Increase ${definition.label} target`} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-navy-200 text-navy-700 hover:bg-navy-50" data-id="element-448" data-mp="2gufv15lc6ryw">
                          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-449" data-mp="1t66fglepv0g7" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2" data-id="element-450" data-mp="26ymd16oum69q">
                      {definition.targetOptions.map(option => <button key={option} type="button" onClick={() => setTarget(goal.category, option)} aria-pressed={goal.weeklyTarget === option} className={`inline-flex h-8 items-center rounded-full border px-3 text-xs font-semibold ${goal.weeklyTarget === option ? 'border-brand-400 bg-brand-50 text-brand-700' : 'border-navy-200 text-navy-600 hover:bg-navy-50'}`} data-id="element-451" data-mp="1egs3d5jra4x4">
                          {option} / week
                        </button>)}
                    </div>
                  </li>;
          })}
            </ul>
          </div> : null}

        {step.id === 'rhythm' ? <fieldset data-id="element-452" data-mp="35j4kefd9qmf7">
            <legend className="text-lg font-bold text-navy-900" data-id="element-453" data-mp="35jmofy66a4xo">
              How many days a week do you prospect?
            </legend>
            <p className="mt-1 text-sm text-navy-500" data-id="element-454" data-mp="fvdp7p6ixfeq">
              Your weekly targets are divided across these days to build the daily
              plan.
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-455" data-mp="2593l26eaivb9">
              {[3, 4, 5, 6].map(option => <button key={option} type="button" aria-pressed={workdays === option} onClick={() => setWorkdays(option)} className={`inline-flex h-11 items-center rounded-lg border px-4 text-sm font-semibold ${workdays === option ? 'border-navy-800 bg-navy-800 text-white' : 'border-navy-200 text-navy-700 hover:bg-navy-50'}`} data-id="element-456" data-mp="20btxyxedivql">
                  {option} days
                </button>)}
            </div>
          </fieldset> : null}

        {step.id === 'review' ? <div data-id="element-457" data-mp="36ypfszs8ykes">
            <h2 className="text-lg font-bold text-navy-900" data-id="element-458" data-mp="36z8n7mk9tqek">
              Your perfect day, derived
            </h2>
            <p className="mt-1 text-sm text-navy-500" data-id="element-459" data-mp="31ykveyo7x5ja">
              This is what will show up on your dashboard each morning.
            </p>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-460" data-mp="23pcuse1ptfb3">
              {enabledGoals.map(goal => {
            const definition = activityById[goal.category];
            const Icon = definition.icon;
            return <li key={goal.category} className="flex items-center gap-3 rounded-xl border border-navy-100 p-4" data-id="element-461" data-mp="1umbrsm85c4bn">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${activityIconClasses[definition.tone]}`} data-id="element-462" data-mp="1kr0k2nv5nxde">
                      <Icon className="h-4 w-4" aria-hidden="true" data-id="element-463" data-mp="1bwvs214acfwa" />
                    </span>
                    <div className="min-w-0" data-id="element-464" data-mp="x04mtb7xk9yr">
                      <p className="text-sm font-semibold text-navy-900" data-id="element-465" data-mp="x2hfaibbe2q2">
                        {definition.cadence === 'daily' ? `${dailyTarget(goal, workdays)} ${definition.unit} / day` : `${goal.weeklyTarget} ${definition.unit} / week`}
                      </p>
                      <p className="truncate text-xs text-navy-500" data-id="element-466" data-mp="sbvhxmbwzo6m">
                        {definition.shortLabel} · {goal.weeklyTarget} per week
                      </p>
                    </div>
                  </li>;
          })}
            </ul>
            <p className="mt-4 rounded-xl bg-navy-50 px-4 py-3 text-xs text-navy-600" data-id="element-467" data-mp="11aqox0ms92az">
              Quarterly equivalent:{' '}
              {enabledGoals.map(goal => `${goal.weeklyTarget * 13} ${activityById[goal.category].shortLabel.toLowerCase()}`).join(' · ')}
            </p>
          </div> : null}

        {step.id === 'import' ? <DatabaseImportStep imports={imports} onChange={(kind: ImportKind, result: ImportResult | null) => setImports(current => ({
        ...current,
        [kind]: result
      }))} data-id="element-468" data-mp="1g1pv6505bere" /> : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-navy-100 px-5 py-4" data-id="element-469" data-mp="1s40jnbixnclv">
        <button type="button" onClick={() => setStepIndex(index => Math.max(0, index - 1))} disabled={stepIndex === 0} className="inline-flex h-11 items-center gap-2 rounded-lg border border-navy-200 px-4 text-sm font-semibold text-navy-700 hover:bg-navy-50 disabled:opacity-40" data-id="element-470" data-mp="1x0r0i12kac8c">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-471" data-mp="2ftl7o8ty1020" />
          Back
        </button>
        <p className="text-xs text-navy-500" data-id="element-472" data-mp="2ks65y2pmx9nt">
          Step {stepIndex + 1} of {steps.length}
        </p>
        {step.id === 'import' ? <button type="button" onClick={handleSave} className="inline-flex h-11 items-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700" data-id="element-473" data-mp="lgvb1jc6xcqx">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-474" data-mp="iwp8ng385uch" />
            {importedCount > 0 ? `Finish setup & import ${importedCount} records` : 'Finish setup'}
          </button> : <button type="button" onClick={() => setStepIndex(index => Math.min(steps.length - 1, index + 1))} disabled={!canAdvance} className="inline-flex h-11 items-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50" data-id="element-475" data-mp="46843nvamobd">
            Continue
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-476" data-mp="mwpwtsg4ljwq" />
          </button>}
      </div>
    </div>;
}